/**
 * useForecast Hook
 *
 * React Query hooks for investment forecast operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import {
  forecastService,
  ForecastResponse,
  GenerateForecastOptions,
} from '../services/forecast.service';
import { unwrapResponse } from '../types';

import { investmentKeys } from './useInvestments';

export const forecastKeys = {
  all: ['forecast'] as const,
  detail: (investmentId: number) => [...forecastKeys.all, investmentId] as const,
};

/**
 * Fetch forecast for an investment
 */
export function useForecast(
  investmentId: number,
  options?: Omit<UseQueryOptions<ForecastResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<ForecastResponse, Error>({
    queryKey: forecastKeys.detail(investmentId),
    queryFn: async () => {
      const response = await forecastService.get(investmentId);
      // Extract data from success wrapper and parse numeric strings
      const responseData = unwrapResponse<ForecastResponse>(response.data);
      return responseData;
    },
    ...options,
  });
}

/**
 * Generate/regenerate forecast for an investment
 */
export function useGenerateForecast(
  investmentId: number,
  options?: UseMutationOptions<ForecastResponse, Error, GenerateForecastOptions | undefined>
) {
  const queryClient = useQueryClient();

  return useMutation<ForecastResponse, Error, GenerateForecastOptions | undefined>({
    mutationFn: async (generateOptions) => {
      const response = await forecastService.generate(investmentId, generateOptions);
      return unwrapResponse<ForecastResponse>(response.data);
    },
    onSuccess: () => {
      // Invalidate forecast cache
      queryClient.invalidateQueries({ queryKey: forecastKeys.detail(investmentId) });
      // Also invalidate related queries
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
    },
    ...options,
  });
}

/**
 * Delete forecast for an investment
 */
export function useDeleteForecast(
  investmentId: number,
  options?: UseMutationOptions<{ message: string; deleted_count: number }, Error, void>
) {
  const queryClient = useQueryClient();

  return useMutation<{ message: string; deleted_count: number }, Error, void>({
    mutationFn: async () => {
      const response = await forecastService.delete(investmentId);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: forecastKeys.detail(investmentId) });
    },
    ...options,
  });
}
